
// Mock implementation of fetch for testing
export const mockFetch = (mockData: any, status = 200) => {
  global.fetch = jest.fn().mockImplementation(() =>
    Promise.resolve({
      ok: status >= 200 && status < 300,
      status,
      json: () => Promise.resolve(mockData),
      text: () => Promise.resolve(JSON.stringify(mockData)),
    })
  );
};

// Reset fetch mock
export const resetFetchMock = () => {
  if (global.fetch && typeof jest !== 'undefined') {
    (global.fetch as jest.Mock).mockReset();
  }
};

// Simulate delay for async operations
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Type for test result
export interface TestResult {
  name: string;
  status: 'success' | 'failure' | 'pending' | 'skipped';
  message?: string;
  details?: string;
  duration?: number;
}

// Run test with timing
export const runTest = async (
  name: string,
  testFn: () => Promise<boolean | { success: boolean; message?: string; details?: string }>,
  options: { timeout?: number; skip?: boolean; } = {}
): Promise<TestResult> => {
  if (options.skip) {
    return {
      name,
      status: 'skipped',
      message: 'Test was skipped',
    };
  }

  const startTime = performance.now();

  try {
    const testPromise = testFn();
    const timeoutPromise = options.timeout
      ? new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`Test timed out after ${options.timeout}ms`)), options.timeout);
      })
      : null;

    const result = timeoutPromise
      ? await Promise.race([testPromise, timeoutPromise])
      : await testPromise;

    const duration = performance.now() - startTime;

    if (typeof result === 'boolean') {
      return {
        name,
        status: result ? 'success' : 'failure',
        message: result ? 'Test passed' : 'Test failed',
        duration,
      };
    } else {
      return {
        name,
        status: result.success ? 'success' : 'failure',
        message: result.message || (result.success ? 'Test passed' : 'Test failed'),
        details: result.details,
        duration,
      };
    }
  } catch (error) {
    const duration = performance.now() - startTime;
    return {
      name,
      status: 'failure',
      message: 'Test threw an exception',
      details: error instanceof Error ? error.stack : String(error),
      duration,
    };
  }
};

export const runTests = async (tests: Array<() => Promise<TestResult>>): Promise<TestResult[]> => {
  const results: TestResult[] = [];
  for (const test of tests) {
    results.push(await test());
  }
  return results;
};

export const elementExists = (selector: string): boolean => {
  return document.querySelector(selector) !== null;
};

export const urlMatches = (pattern: RegExp): boolean => {
  return pattern.test(window.location.href);
};

// Stubs for removed Firebase helpers
export const getFirestoreData = async (collectionName: string, docId?: string) => {
  console.warn('getFirestoreData is deprecated/stubbed in local mode');
  return null;
};

export const queryFirestoreData = async (collectionName: string, fieldPath: string, value: any) => {
  console.warn('queryFirestoreData is deprecated/stubbed in local mode');
  return [];
};

export const assert = <T>(value: T) => {
  return {
    toBe: (expected: T): boolean => {
      if (value !== expected) throw new Error(`Expected ${expected} but received ${value}`);
      return true;
    },
    toContain: (expected: any): boolean => {
      if (Array.isArray(value)) {
        if (!value.includes(expected)) throw new Error(`Expected array to contain ${expected}`);
        return true;
      } else if (typeof value === 'string') {
        if (!value.includes(expected)) throw new Error(`Expected string to contain ${expected}`);
        return true;
      }
      throw new Error('toContain can only be used with arrays or strings');
    },
    toBeDefined: (): boolean => {
      if (value === undefined) throw new Error('Expected value to be defined');
      return true;
    },
    toBeNull: (): boolean => {
      if (value !== null) throw new Error(`Expected null but received ${value}`);
      return true;
    },
    toBeTruthy: (): boolean => {
      if (!value) throw new Error(`Expected truthy value but received ${value}`);
      return true;
    },
    toBeFalsy: (): boolean => {
      if (!!value) throw new Error(`Expected falsy value but received ${value}`);
      return true;
    },
    toBeGreaterThan: (expected: number): boolean => {
      if (typeof value !== 'number') throw new Error(`Expected number`);
      if (value <= expected) throw new Error(`Expected > ${expected}`);
      return true;
    },
    toBeLessThan: (expected: number): boolean => {
      if (typeof value !== 'number') throw new Error(`Expected number`);
      if (value >= expected) throw new Error(`Expected < ${expected}`);
      return true;
    },
    toEqual: (expected: any): boolean => {
      if (JSON.stringify(value) !== JSON.stringify(expected)) throw new Error(`Expected equal`);
      return true;
    },
  };
};

export const userActions = {
  click: (selector: string): boolean => {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) throw new Error(`Element not found: ${selector}`);
    element.click();
    return true;
  },
  setValue: (selector: string, value: string): boolean => {
    const element = document.querySelector(selector) as HTMLInputElement;
    if (!element) throw new Error(`Element not found: ${selector}`);
    element.value = value;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  },
  select: (selector: string, value: string): boolean => {
    const element = document.querySelector(selector) as HTMLSelectElement;
    if (!element) throw new Error(`Element not found: ${selector}`);
    element.value = value;
    element.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  },
  check: (selector: string, checked: boolean): boolean => {
    const element = document.querySelector(selector) as HTMLInputElement;
    if (!element) throw new Error(`Element not found: ${selector}`);
    element.checked = checked;
    element.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  },
  submit: (formSelector: string): boolean => {
    const form = document.querySelector(formSelector) as HTMLFormElement;
    if (!form) throw new Error(`Form not found: ${formSelector}`);
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    form.dispatchEvent(submitEvent);
    return !submitEvent.defaultPrevented;
  }
};
